<template>
    <grid-area area="side-sheet-header">
        <div class="lead-detail-header">
            <button-outline-icon
                v-if="false"
                :icon="hasNotifications ? IconVisibility : IconVisibilityOff"
                :color="hasNotifications ? Color.brandGreen : Color.gray400"
            >
                Houdt mij op de hoogte van fase en status wijzigingen bij deze lead
            </button-outline-icon>

            <div class="lead-detail-header__content">
                <flex-container>
                    <text-heading sub>
                        <span>{{ props.lead.pipeline.name }}</span>

                        <template v-if="Status.OPEN.equals(props.lead.status)">
                            <span> &bull; </span>
                            <text-decorator :size="FontSize.sm" :color="Color.gray400">
                                <span v-if="props.lead.onSchedule">Lopend: op schema</span>
                                <span v-else>Lopend: niet op schema</span>
                            </text-decorator>
                        </template>
                    </text-heading>
                </flex-container>

                <flex-container
                    :gap="Size.sm"
                    justify-content="flex-end"
                    align-items="center"
                >
                    <button-text
                        v-if="props.lead"
                        :disabled="!isEditable"
                        @click="navigateToCreateAppointment"
                    >
                        <svg-icon
                            :icon="IconCalendarAddOn"
                        />
                        <span>Afspraak toevoegen</span>
                    </button-text>

                    <button-text
                        v-if="props.lead"
                        :disabled="!isEditable"
                        @click="navigateToCreateTask"
                    >
                        <svg-icon
                            :icon="IconAddTask"
                        />
                        <span>Taak toevoegen</span>
                    </button-text>

                    <div class="lead-detail-header__security">
                        <collapse-box-decorator
                            trigger="click"
                            close-trigger="click"
                            horizontal-position="right"
                        >
                            <template #default="{ bindings }">
                                <button-default v-bind="bindings">
                                    <svg-icon
                                        :icon="IconSecurity"
                                        :color="Color.brandGreen"
                                    />
                                </button-default>
                            </template>

                            <template #title>
                                Toegang
                            </template>

                            <template #content>
                                <flex-container :padding="Size.sm" :gap="Size.sm">
                                    <flex-container direction="column" class="lead-detail-header__permissions">
                                        <text-decorator bold :size="FontSize.sm">
                                            Standaard
                                        </text-decorator>

                                        <data-group>
                                            <data-label>Aangemaakt</data-label>
                                            <data-value>{{ props.lead?.referrer.team.name }} (Team)</data-value>
                                        </data-group>

                                        <data-group>
                                            <data-label>Verantwoordelijk</data-label>
                                            <data-value>{{ props.lead?.assignee.team.name }} (Team)</data-value>
                                        </data-group>

                                        <horizontal-line no-margin />

                                        <flex-container justify-content="space-between" align-items="center">
                                            <text-decorator bold :size="FontSize.sm">
                                                Aanvullend
                                            </text-decorator>

                                            <button-default @click="showAddPermissionEntry">
                                                <svg-icon
                                                    :icon="IconAdd"
                                                    :color="Color.green500"
                                                />
                                            </button-default>
                                        </flex-container>

                                        <flex-container v-if="showingAddPermissionEntry">
                                            <input-group v-model="selectedPermissionEntry">
                                                <template #input>
                                                    <select-input
                                                        :items="companyUsersAndTeams"
                                                        searchable
                                                    >
                                                        <template #item="{ bindings }">
                                                            <flex-container :gap="Size.xs">
                                                                <div>
                                                                    {{ bindings.item.label }}
                                                                </div>

                                                                <text-decorator
                                                                    :size="FontSize.xs"
                                                                    :color="Color.gray400"
                                                                >
                                                                    <template v-if="bindings.item.type === 'user'">
                                                                        (Gebruiker)
                                                                    </template>
                                                                    <template v-if="bindings.item.type === 'team'">
                                                                        (Team)
                                                                    </template>
                                                                </text-decorator>
                                                            </flex-container>
                                                        </template>
                                                    </select-input>
                                                </template>
                                            </input-group>

                                            <flex-container justify-content="flex-end">
                                                <button-outline @click="hideAddPermissionEntry">
                                                    Annuleren
                                                </button-outline>

                                                <button-block-confirm :disabled="savingReferralPermission" @confirmed="createReferralPermission">
                                                    <template #default>
                                                        Toevoegen
                                                    </template>
                                                    <template #confirmation>
                                                        Bevestig
                                                    </template>
                                                </button-block-confirm>
                                            </flex-container>
                                        </flex-container>

                                        <horizontal-line v-if="showingAddPermissionEntry && leadPermissions.count.value > 0" no-margin />

                                        <div class="lead-detail-header__permissions-list">
                                            <template v-if="teamLeadPermissions.length > 0">
                                                <text-decorator bold :color="Color.gray400">
                                                    Teams
                                                </text-decorator>

                                                <flex-container :gap="Size.xs">
                                                    <flex-container
                                                        v-for="permission in teamLeadPermissions"
                                                        :key="permission.id.value"
                                                        justify-content="space-between"
                                                        align-items="center"
                                                    >
                                                        <text-decorator :size="FontSize.sm">
                                                            {{ permission.team?.name }}
                                                        </text-decorator>

                                                        <button-default @click="deleteReferralPermission(permission)">
                                                            <svg-icon
                                                                :icon="IconRemove"
                                                                :color="Color.red500"
                                                            />
                                                        </button-default>
                                                    </flex-container>
                                                </flex-container>
                                            </template>
                                            <template v-if="userLeadPermissions.length > 0">
                                                <text-decorator bold :color="Color.gray400">
                                                    Gebruikers
                                                </text-decorator>

                                                <flex-container :gap="Size.xs">
                                                    <flex-container
                                                        v-for="permission in userLeadPermissions"
                                                        :key="permission.id.value"
                                                        justify-content="space-between"
                                                        align-items="center"
                                                    >
                                                        <text-decorator :size="FontSize.sm">
                                                            {{ permission.user?.name }}
                                                        </text-decorator>

                                                        <button-default @click="deleteReferralPermission(permission)">
                                                            <svg-icon
                                                                :icon="IconRemove"
                                                                :color="Color.red500"
                                                            />
                                                        </button-default>
                                                    </flex-container>
                                                </flex-container>
                                            </template>
                                        </div>
                                    </flex-container>
                                </flex-container>
                            </template>
                        </collapse-box-decorator>
                    </div>

                    <div class="lead-detail-header__actions">
                        <collapse-box-decorator
                            trigger="click"
                            horizontal-position="right"
                        >
                            <template #default="{ bindings }">
                                <button-default
                                    v-bind="bindings"
                                >
                                    <svg-icon
                                        :icon="IconManageAccounts"
                                        :color="Color.brandGreen"
                                    />
                                </button-default>
                            </template>

                            <template #title>
                                Acties
                            </template>

                            <template #content>
                                <collapse-option-container>
                                    <collapse-option v-if="canReopen" @click="redirectToReopenLead">
                                        Lead heropenen
                                    </collapse-option>
                                    <collapse-option @click="redirectToForwardLead">
                                        Toevoegen aan andere pijplijn
                                    </collapse-option>
                                    <collapse-option v-if="isEditable && currentUser?.role.equals(Role.COMPANY_MANAGER)" @click="confirmLeadDelete">
                                        <text-decorator :color="Color.red500">
                                            Lead verwijderen
                                        </text-decorator>
                                    </collapse-option>
                                </collapse-option-container>
                            </template>
                        </collapse-box-decorator>

                        <confirmation-dialog
                            ref="deleteConfirmDialog"
                            label="Weet u zeker dat u deze lead wilt verwijderen?"
                            :description="
                                `U probeert de lead ${props.lead.pipeline.name } van ${props.lead.assignee.team.name} te verwijderen, dit kan niet teruggedraaid worden. Weet u dit zeker?`
                            "
                        >
                            <template #default>
                                <text-heading>Weet u zeker dat u deze lead wilt verwijderen?</text-heading>
                                <p>
                                    U probeert de lead {{ props.lead.pipeline.name }} van {{ props.lead.assignee.team.name }} te verwijderen, dit kan niet teruggedraaid worden.<br>
                                    Weet u dit zeker?
                                </p>
                            </template>
                            <template #actions="{ bindings }">
                                <button-outline @click="bindings.overlay.closeOverlay();">
                                    Annuleren
                                </button-outline>
                                <button-block
                                    @loading="deleteLead($event)"
                                    @completed="bindings.overlay.closeOverlay()"
                                >
                                    Verwijderen
                                </button-block>
                            </template>
                        </confirmation-dialog>
                    </div>
                </flex-container>

                <flex-container class="lead-detail-header__properties" :gap="Size.xs">
                    <div
                        v-if="!isEditable"
                        class="lead-detail-header__property"
                    >
                        <channel-label :channel="channel" />
                    </div>
                    <input-group
                        v-else
                        v-model="channel"
                        inline
                    >
                        <template #input>
                            <channel-select-input
                                :channels="Channel.values"
                                :disabled="channelDisabled"
                                variant="inline"
                            />
                        </template>
                    </input-group>

                    <div class="lead-detail-header__property">
                        &bull;
                    </div>

                    <div
                        v-if="!isEditable"
                        class="lead-detail-header__property"
                    >
                        {{ source.name }}
                    </div>
                    <input-group
                        v-else
                        v-model="source"
                        inline
                    >
                        <template #input>
                            <source-select-input
                                :sources="sourceCollection.data.value"
                                :disabled="sourceDisabled"
                                variant="inline"
                            />
                        </template>
                    </input-group>
                </flex-container>

                <flex-container justify-content="flex-end">
                    <text-decorator :size="FontSize.xs">
                        <text-decorator :color="Color.gray400">
                            Laatst bekeken:
                        </text-decorator>
                        <text-decorator>
                            {{ props.lead?.viewedAtLabel }}
                        </text-decorator>
                    </text-decorator>
                </flex-container>
            </div>
        </div>
    </grid-area>
</template>

<script setup lang="ts">
    import {
        ButtonBlock,
        ButtonBlockConfirm,
        ButtonDefault,
        ButtonOutline,
        ButtonOutlineIcon,
        ButtonState,
        type ButtonStateChangedEvent,
        ButtonText,
        CollapseBoxDecorator,
        CollapseOption,
        CollapseOptionContainer,
        Color,
        ConfirmationDialog,
        DataGroup,
        DataLabel,
        DataValue,
        type ExposedOverlay,
        FlexContainer,
        FontSize,
        HorizontalLine,
        InputGroup,
        SelectInput,
        Size,
        SvgIcon,
        TextDecorator,
        TextHeading,
        useInteractionBlocker,
    } from '@keyvue/one-ui';

    import {
        Channel,
        type CompanySubResource,
        Lead,
        LeadPermission,
        LeadPermissionId,
        Role,
        Source,
        Status,
        Team,
        TeamType,
        User,
        UserType,
    } from '@keyvue/one-api-client';

    import {
        createResourceIdentifier,
        Referral,
        ReferralAPI,
        ReferralPermissionAPI,
    } from '@keyvue/one-api-client-legacy';

    import IconManageAccounts from '@keyvue/one-material-icons/icons/manage_accounts.svg?component';
    import IconSecurity from '@keyvue/one-material-icons/icons/security.svg?component';
    import IconVisibility from '@keyvue/one-material-icons/icons/visibility.svg?component';
    import IconVisibilityOff from '@keyvue/one-material-icons/icons/visibility_off.svg?component';
    import IconRemove from '@keyvue/one-material-icons/icons/close.svg?component';
    import IconAdd from '@keyvue/one-material-icons/icons/add.svg?component';
    import IconCalendarAddOn from '@keyvue/one-material-icons/icons/calendar_add_on.svg?component';
    import IconAddTask from '@keyvue/one-material-icons/icons/add_task.svg?component';

    import { computed, type Ref, ref, watch } from 'vue';
    import ChannelSelectInput from '@components/Inputs/ChannelSelectInput.vue';
    import SourceSelectInput from '@components/Inputs/SourceSelectInput.vue';
    import ChannelLabel from '@components/Labels/ChannelLabel.vue';
    import useClient from '@composables/useClient';
    import useNotify from '@composables/useNotify';
    import { useRouter } from 'vue-router';
    import useEventBus from '@composables/useEventBus';
    import useParentRoute from '@composables/useParentRoute';
    import useLeadSecurity from '@composables/security/useLeadSecurity';

    interface Props {
        lead: Lead;
    }

    const props = defineProps<Props>();

    const { getReactiveCollection, currentCompany, currentUser, client } = useClient();
    const router = useRouter();
    const createRouteLocation = useParentRoute();
    const { notifySaved, notifySaving, notifyError, notifyDeleted } = useNotify();
    const { blockInteractionForLoad } = useInteractionBlocker();
    const eventBus = useEventBus();

    const deleteConfirmDialog = ref<ExposedOverlay>();
    const channel = ref(props.lead.origin.channel) as Ref<Channel>;
    const channelDisabled = ref(false);
    const source = ref(props.lead.origin.source) as Ref<Source>;
    const sourceDisabled = ref(false);

    const { isUserOfAssignedCompany, canReopen } = await useLeadSecurity(props.lead);
    const isEditable = computed(() => isUserOfAssignedCompany.value);

    const sourceCollection = await getReactiveCollection(Source, {
        channel: channel.value.toString(),
        company: currentCompany.value?.id,
        'order[name]': 'asc',
    });

    const hasNotifications = computed(() => false);

    const redirectToReopenLead = () => {
        router.push({
            name: 'contactReopenLead',
        });
    };

    const redirectToForwardLead = () => {
        router.push({
            name: 'contactDuplicateLead',
        });
    };

    const redirectToContactDetail = () => {
        router.push(createRouteLocation({
            name: 'contactDetail',
        }));
    };

    const confirmLeadDelete = () => {
        deleteConfirmDialog.value?.openOverlay();
    };

    const deleteLead = async (event: ButtonStateChangedEvent) => {
        event.button.setState(ButtonState.completed);
        deleteConfirmDialog.value?.closeOverlay();
        const closeBlocker = blockInteractionForLoad('De lead wordt verwijderd; dit proces duurt 20-30 seconden');
        await ReferralAPI.delete(props.lead.id.value);
        eventBus.dispatch('resource-deleted', 'Lead');
        closeBlocker();
        redirectToContactDetail();
    };

    const changeReferralField = async (callback: (referral: Referral) => Referral) => {
        const closeNotification = notifySaving(undefined, 0);
        const legacyReferral = await ReferralAPI.get(props.lead.id.value);
        await ReferralAPI.update(props.lead.id.value, callback(legacyReferral));
        eventBus.dispatch('resource-manipulated', 'Lead');
        notifySaved();
        if (closeNotification) {
            closeNotification();
        }
    };

    const leadPermissions = await getReactiveCollection(LeadPermission, {
        lead: props.lead.id.value,
    });

    const userLeadPermissions = computed(() => leadPermissions.data.value?.filter((permission) => !!permission.user));
    const teamLeadPermissions = computed(() => leadPermissions.data.value?.filter((permission) => !!permission.team));

    if (!currentCompany.value) {
        throw new Error('currentCompany is not provided');
    }

    const showingAddPermissionEntry = ref(false);
    const showAddPermissionEntry = () => {
        showingAddPermissionEntry.value = true;
    };

    const hideAddPermissionEntry = () => {
        selectedPermissionEntry.value = null;
        showingAddPermissionEntry.value = false;
    };

    const selectedPermissionEntry = ref<{ value: string, label: string, type: string } | null>(null);
    const companyUsers = (await client.getSubResources<CompanySubResource, User>(currentCompany.value, User, 'users', { pagination: false })).data;
    const companyTeams = (await client.getSubResources<CompanySubResource, Team>(currentCompany.value, Team, 'teams', { pagination: false })).data;
    const companyUsersAndTeams = computed(() => [
        ...companyTeams
            .filter((team: Team) => team.type.equals(TeamType.DEFAULT) && team.active)
            .filter((team: Team) => !leadPermissions.data.value.some((permission) => {
                if (!permission.team) {
                    return false;
                }

                return permission.team.id.value === team.id.value;
            }))
            .map((team: Team) => ({
                value: team.id,
                label: team.name,
                type: 'team',
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        ...companyUsers
            .filter((user: User) => user.type.equals(UserType.DEFAULT) && user.active)
            .filter((user: User) => !leadPermissions.data.value.some((permission) => {
                if (!permission.user) {
                    return false;
                }

                return permission.user.id.value === user.id.value;
            }))
            .map((user: User) => ({
                value: user.id,
                label: user.name.fullName,
                type: 'user',
            }))
            .sort((a, b) => a.label?.localeCompare(b.label)),
    ]);

    const savingReferralPermission = ref(false);
    const createReferralPermission = async (event: ButtonStateChangedEvent) => {
        if (!selectedPermissionEntry.value) {
            return;
        }

        const closeNotification = notifySaving(undefined, 0);
        try {
            savingReferralPermission.value = true;
            await ReferralPermissionAPI.create({
                id: LeadPermissionId.generate().value,
                referral: createResourceIdentifier('referrals', props.lead.id.value),
                user: selectedPermissionEntry.value.type === 'user' ? createResourceIdentifier('users', selectedPermissionEntry.value.value) : null,
                branch: selectedPermissionEntry.value.type === 'team' ? createResourceIdentifier('branches', selectedPermissionEntry.value.value) : null,
            } as any);
            await new Promise((resolve) => {
                setTimeout(resolve, 1000);
            });
            selectedPermissionEntry.value = null;
            showingAddPermissionEntry.value = false;
            await leadPermissions.load();
            eventBus.dispatch('resource-manipulated', 'LeadPermission');
            notifySaved('Aanvullende toegang verleend');
        } catch (error) {
            notifyError(error);
        } finally {
            savingReferralPermission.value = false;
            if (closeNotification) {
                closeNotification();
            }
            event.button.setState(ButtonState.completed);
        }
    };

    const deleteReferralPermission = async (permission: LeadPermission) => {
        const closeNotification = notifySaving(undefined, 0);
        try {
            await ReferralPermissionAPI.delete(permission.id.value);
            await new Promise((resolve) => {
                setTimeout(resolve, 1000);
            });
            await leadPermissions.load();
            eventBus.dispatch('resource-manipulated', 'LeadPermission');
            notifyDeleted('Aanvullende toegang ingetrokken');
        } catch (error) {
            notifyError(error);
        } finally {
            if (closeNotification) {
                closeNotification();
            }
        }
    };

    watch(channel, async (newChannel, oldChannel) => {
        if (newChannel.toString() === oldChannel.toString()) {
            return;
        }

        channelDisabled.value = true;
        sourceDisabled.value = true;

        await sourceCollection.load({
            channel: newChannel.toString(),
            company: currentCompany.value?.id,
            'order[name]': 'asc',
        });

        await changeReferralField((referral) => {
            const localReferral = referral;
            localReferral.channel = newChannel.value.toString();
            return localReferral;
        });

        eventBus.dispatch('resource-manipulated', 'Lead');

        channelDisabled.value = false;
        sourceDisabled.value = false;
    });

    watch(source, async (newSource, oldSource) => {
        if (newSource.equals(oldSource)) {
            return;
        }

        sourceDisabled.value = true;

        await changeReferralField((referral) => {
            const localReferral = referral;
            localReferral.newSource = createResourceIdentifier('sources', newSource.id.toString());
            return localReferral;
        });

        eventBus.dispatch('resource-manipulated', 'Lead');

        sourceDisabled.value = false;
    });

    const navigateToCreateAppointment = () => {
        router.push(createRouteLocation({
            name: 'ActionCreate',
            params: {
                leadId: props.lead.id.value,
            },
        }, 'lead'));
    };

    const navigateToCreateTask = () => {
        router.push(createRouteLocation({
            name: 'ActionCreate',
            params: {
                leadId: props.lead.id.value,
            },
            query: {
                type: 'task',
            },
        }, 'lead'));
    };
</script>

<style scoped lang="scss">
.lead-detail-header {
    width: 100%;
    padding: size(md);
    padding-top: 12px; // uitlijnen
    display: grid;

    grid-template-columns: 1fr;
    // @todo: wanneer oogje aan gaat
    // grid-template-columns: min-content 1fr;

    gap: size(md);
    border-bottom: 1px solid color(gray-300);

    &__content {
        gap: size(sm) 0;
        display: grid;
        grid-template-columns: auto 1fr;
        justify-content: space-between;
        justify-items: center;
        align-content: space-between;
        align-items: center;
    }

    &__actions {
        margin-right: size(lg);
        padding-right: size(sm);
        border-right: 1px solid color(gray-300);
    }

    &__security {
        margin-left: size(sm);
        padding-left: size(sm);
        border-left: 1px solid color(gray-300);
    }

    &__properties {
        font-size: font-size(xs);
    }

    &__property {
        display: flex;
        align-items: center;
        align-content: center;
        gap: size(sm);
    }

    &__permissions {
        min-width: 15vw !important;
        max-width: 30vw;
    }

    &__permissions-list {
        display: flex;
        flex-direction: column;
        gap: size(sm);
    }
}
</style>
